/**
 * Board-agent UI state (F-144) — one store per open board view, owned by
 * BoardScreen like the design and comments stores (see `design/store.ts` for
 * why there is no panel registry).
 *
 * Everything here is LOCAL view state. The conversation is not persisted
 * anywhere: the server holds nothing between turns (the panel replays the
 * transcript as `history`), and the board itself only ever receives what a
 * plan wrote. The APPLIED RUNS live here too — they are what the Revert
 * buttons act on — and they are as long-lived as the Ctrl+Z stack they
 * complement: this board view. Navigating away drops both.
 */
import type { AgentAttachment, AgentPlan, ImportStepTask } from "@kola/shared";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { AppliedRun } from "./apply.js";
import type { ImportTasks } from "./import-tasks.js";

export type AgentPlanState = "proposed" | "applying" | "applied" | "discarded" | "reverting" | "reverted";

export interface AgentTurn {
  id: string;
  role: "user" | "assistant";
  text: string;
  /** User turn: the files it carried (name + size, the text is not kept). */
  attachments?: { name: string; chars: number }[];
  /** Assistant turn: the proposed edits, if any. */
  plan?: AgentPlan;
  planState?: AgentPlanState;
  /** The run an applied plan became — key into `runs`. */
  runId?: string;
  /** Assistant turn: the model that actually answered. */
  model?: string;
  /** Assistant turn that is an error message rather than an answer. */
  error?: boolean;
  /** F-150: the turn that imported this file as it is — applying it leaves the step tasks. */
  importFile?: string;
  /** F-150: the step task this turn proposes. */
  importStep?: ImportStepTask;
}

export interface AgentUiState {
  panelOpen: boolean;
  turns: AgentTurn[];
  /** A turn is in flight (the server is thinking). */
  pending: boolean;
  draft: string;
  /** Files attached to the NEXT message. */
  attachments: AgentAttachment[];
  /** The panel's model pick (`""` = the function default). */
  model: string;
  runs: Record<string, AppliedRun>;
  /** F-150: the file import's step tasks for this board (persisted per board). */
  importTasks: ImportTasks | null;

  setPanelOpen(open: boolean): void;
  setDraft(draft: string): void;
  setModel(model: string): void;
  setPending(pending: boolean): void;
  addAttachment(attachment: AgentAttachment): void;
  removeAttachment(id: string): void;
  clearAttachments(): void;
  pushTurn(turn: AgentTurn): void;
  updateTurn(id: string, patch: Partial<AgentTurn>): void;
  setRun(run: AppliedRun): void;
  setImportTasks(tasks: ImportTasks | null): void;
  clearConversation(): void;
}

export type AgentUiStore = StoreApi<AgentUiState>;

export function createAgentUiStore(initialModel = "", initialImportTasks: ImportTasks | null = null): AgentUiStore {
  return createStore<AgentUiState>((set) => ({
    panelOpen: false,
    turns: [],
    pending: false,
    draft: "",
    attachments: [],
    model: initialModel,
    runs: {},
    importTasks: initialImportTasks,

    setPanelOpen: (panelOpen) => set({ panelOpen }),
    setDraft: (draft) => set({ draft }),
    setModel: (model) => set({ model }),
    setPending: (pending) => set({ pending }),
    addAttachment: (attachment) => set((s) => ({ attachments: [...s.attachments, attachment] })),
    removeAttachment: (id) => set((s) => ({ attachments: s.attachments.filter((a) => a.id !== id) })),
    clearAttachments: () => set({ attachments: [] }),
    pushTurn: (turn) => set((s) => ({ turns: [...s.turns, turn] })),
    updateTurn: (id, patch) =>
      set((s) => ({ turns: s.turns.map((t) => (t.id === id ? { ...t, ...patch } : t)) })),
    setRun: (run) => set((s) => ({ runs: { ...s.runs, [run.id]: run } })),
    setImportTasks: (importTasks) => set({ importTasks }),
    // Runs are kept: a cleared transcript must not strand an applied plan
    // with no Revert — the card is gone, but Ctrl+Z still knows the run.
    clearConversation: () => set({ turns: [], attachments: [], draft: "" }),
  }));
}
